import { action, observable, computed, autorun } from 'mobx';
import axios from 'axios';
import isPlainObject from 'lodash/isPlainObject';
import * as Sentry from '@sentry/react';

import { modalStore } from '~/common/stores/ui/modal.js';
import { loginStore } from '~/common/stores/ui/loginStore';
import { formsStore } from '~/common/stores/ui/forms';
// FIXME: remove afer pricing test is over
import { offerSuiteStore } from '~/common/stores/domain/offerSuite';

import userInfo from '~/common/services/userInfo';
import userNumber from '~/common/services/userNumber';
import trialUsersCount from '~/common/services/trialUserCount';

import { isString, isObject, isBoolean } from '~/common/utils/typeCheckers';

import {
    PLAN_TERM_MONTHLY, PLAN_TERM_ANNUAL, PAYMENT_ESTIMATE_NO_TRIAL_ACCOUNT, TRIAL_MAX_AGE_FTAGE, TRIAL_MAX_AGE_EXPIRY_DATE, OFFER_SUITE_ANNUAL, OFFER_SUITE_WITH_TRUE_SOLO
} from '~/common/utils/consts.js';
import { convertNumberFormat } from '~/common/utils/numberOperations.js';
import deepGet from '~/common/utils/deepGet.js';
import { ghIsTransferNeeded, ghFreeTrialAge } from '~/common/globals.js';
import paymentEstimateObject from '~/common/utils/paymentEstimateObject';
import { errorPurchaseEvent, gtm, loginEvent, userInformationEvent } from '~/common/utils/tracking';

class ConvertToPaidClass {
    @observable isUserConvertingToPaid = false;
    @observable canTrialBeConverted = true;

    @observable invalidAddress = false;
    // payment estimate vars
    @observable calculatingTaxes = false;
    @observable estimatePaymentError = '';
    @observable orderTotal = {};

    @observable creatingAccount = false;
    @observable signupError = '';

    @observable timeoutErrorStep = '';
    @observable freeTrialAge = '';
    @observable isTransferNeeded = null;
    @observable planWithNotEnoughUsersError = false;
    @observable trialHasOldPrices = false;
    @observable trialUsersAndExtensions = {};
    @observable isTrialPassiveExpired = false;
    @observable isTrialExpired = false;
    @observable trialConverted = false;

    bootstrapStore() {
        // check for trial age
        autorun((rctn) => {
            const freeTrialAge = ghFreeTrialAge();
            // grab age of the trial
            if (freeTrialAge) {
                this.setFreeTrialAge(freeTrialAge);
                rctn.dispose();
            }
        });

        // check for is transfer is needed from the url
        autorun((rctn) => {
            // set the is transfer is needed flag
            if (ghIsTransferNeeded()) {
                if (ghIsTransferNeeded().toLowerCase() === 'true') this.setIsTransferNeeded(true);
                if (ghIsTransferNeeded().toLowerCase() === 'false') this.setIsTransferNeeded(false);
                rctn.dispose();
            }
        });
    }

    @action clearTrialFlow() {
        // flush users info
        this.setIsUserConvertingToPaid(false);
        loginStore.setUserInfo({});
        loginStore.setTrialNumberInfo({});
        this.setInvalidAddress(false);
        this.setOrderTotal({});

        if (modalStore.childComponentString === 'loginModal') modalStore.setModalOptions({ isModalOpen: this.canTrialBeConverted });
    }

    @action setTrialConverted(bool) {
        this.trialConverted = isBoolean(bool, this.trialConverted);
    }

    @action setTimeoutErrorStep(str) {
        this.timeoutErrorStep = isString(str, this.timeoutErrorStep);
    }

    @action setIsUserConvertingToPaid(bool) {
        this.isUserConvertingToPaid = isBoolean(bool, this.isUserConvertingToPaid);
    }

    @action setCanTrialBeConverted(bool) {
        this.canTrialBeConverted = isBoolean(bool, this.canTrialBeConverted);
    }

    // set flag saying we're calculating taxes
    @action setCalculatingTaxes(bool) {
        this.calculatingTaxes = isBoolean(bool, this.calculatingTaxes);
    }

    @action setEstimatePaymentError(str) {
        this.estimatePaymentError = isString(str, this.estimatePaymentError);
    }

    @action setInvalidAddress(bool) {
        this.invalidAddress = isBoolean(bool, this.invalidAddress);
    }

    // set order total
    @action setOrderTotal(obj) {
        this.orderTotal = isObject(obj, this.orderTotal);
    }

    @action setCreatingAccount(bool) {
        this.creatingAccount = isBoolean(bool, this.creatingAccount);
    }

    @action setSignupError(str) {
        this.signupError = isString(str, this.signupError);
    }

    @action setFreeTrialAge(str) {
        this.freeTrialAge = isString(str, this.freeTrialAge);
    }

    @action setIsTransferNeeded(bool) {
        this.isTransferNeeded = isBoolean(bool, this.isTransferNeeded);
    }

    @action setPlanWithNotEnoughUsersError(bool) {
        this.planWithNotEnoughUsersError = isBoolean(bool, this.planWithNotEnoughUsersError);
    }

    @action setTrialHasOldPrices(bool) {
        this.trialHasOldPrices = isBoolean(bool, this.trialHasOldPrices);
    }

    @action setIstransferNeededFromAPI(urlLink) {
        if (urlLink) {
            // grab everything after the itn param and remove everything after the & if it exists
            const param = urlLink.split('itn=')[1]?.split('&')[0];
            // if the string is equals to true
            const isTransferNeeded = param?.toLowerCase() === 'true';
            // set flag
            this.setIsTransferNeeded(isTransferNeeded);
        }
    }

    @action setTrailAgeFromAPI(urlLink) {
        if (urlLink) {
            // grab everything after the ftage param and remove everything after the & if it exists
            const param = urlLink.split('ftage=')[1].split('&')[0];
            // set flag
            if (param) this.setFreeTrialAge(param);
        }
    }

    @action setTrialUsersAndExtensions(data) {
        this.trialUsersAndExtensions = isObject(data, this.trialUsersAndExtensions);
    }

    // Is trial expired within 10-28 days and still able to get User Details
    @action setTrialPassiveExpired(bool) {
        this.isTrialPassiveExpired = isBoolean(bool, this.isTrialPassiveExpired);
    }

    // Set if trial is Expired or not
    @action setTrialExpired(bool) {
        this.isTrialExpired = isBoolean(bool, this.isTrialExpired);
    }

    // log in user to get auth token
    @action authUser(callBack) {
        const { Email, PasswordLogin } = formsStore;
        formsStore.clearFieldError('Email');
        formsStore.clearFieldError('PasswordLogin');

        this.setCanTrialBeConverted(true);

        loginStore.getVerificationCode({ userIdentification: Email, PasswordLogin }, (error) => {
            const errorResponseData = deepGet(error, ['response', 'data']);
            const errorString = deepGet(errorResponseData, ['Errors', 0]);
            const urlLink = deepGet(errorResponseData, ['CancelledAccountOptionsLink']);
            // try to get is transfer needed flag
            if (this.isTransferNeeded === null) this.setIstransferNeededFromAPI(urlLink);
            // try to get trial age from api
            if (this.freeTrialAge === '') this.setTrailAgeFromAPI(urlLink);

            if (typeof errorString === 'string') {
                const errorWithCode = errorString.includes('Error') && errorString.split(':');
                // if trial has expired still set token
                if (errorString.includes('FreeTrial Account is Canceled')) {
                    // extract token from response string
                    const token = errorString.split('Token:');
                    const authToken = token[1];
                    // set token
                    loginStore.setAuthToken(authToken);
                    this.getUserInfo();
                    this.setTrialPassiveExpired(true);
                    if (callBack) callBack(authToken);
                } else if (errorWithCode) {
                    if (errorWithCode[0] === 'Error 1010' || errorWithCode[0] === 'Error 1006') {
                        loginStore.setShowVerification(false);
                        loginStore.setResendInterval(0);
                        loginStore.setLoginError(errorWithCode[1].trim());
                    } else {
                        loginStore.setLoginError(errorWithCode[1].trim());
                    }
                } else {
                    // error was from invalid user credentials, alert user
                    loginStore.setLoginError(errorString);

                    // loginStore.setLoginError('Your email address and/or password are incorrect.');
                }
            } else {
                // report a general error and alert user
                loginStore.setLoginError('Something went wrong. Please try again.');
            }
            Sentry.captureMessage('userLoginError', { extra: {
                errorObj: errorResponseData
            } });
        });
    }

    @action validateUser(code, callBack) {
        loginStore.validateUser(code, (authToken) => {
            this.getUserInfo();
            if (callBack) callBack(authToken);
        }, (error) => {
            const errorResponseData = deepGet(error, ['response', 'data']);
            const errorString = deepGet(errorResponseData, ['Errors', 0]);

            const urlLink = deepGet(errorResponseData, ['CancelledAccountOptionsLink']);
            // try to get is transfer needed flag
            if (this.isTransferNeeded === null) this.setIstransferNeededFromAPI(urlLink);
            // try to get trial age from api
            if (this.freeTrialAge === '') this.setTrailAgeFromAPI(urlLink);

            if (typeof errorString === 'string') {
                const errorWithCode = errorString.includes('Error') && errorString.split(':');
                // if trial has expired still set token
                if (errorString.includes('FreeTrial Account is Canceled')) {
                    // extract token from response string
                    const token = errorString.split('Token:');
                    const authToken = token[1];
                    // set token
                    loginStore.setAuthToken(authToken);
                    this.getUserInfo();
                    this.setTrialPassiveExpired(true);
                    if (callBack) callBack(authToken);
                } else if (errorWithCode) {
                    if (errorWithCode[0] === 'Error 1010' || errorWithCode[0] === 'Error 1006') {
                        loginStore.setShowVerification(false);
                        loginStore.setResendInterval(0);
                        loginStore.setLoginError(errorWithCode[1].trim());
                    } else {
                        loginStore.setAuthError(errorWithCode[1].trim());
                    }
                }
            } else {
                loginStore.setAuthError('Verification failed. Try again or resend a new code.');
            }
            // report a general error and alert user
            Sentry.captureMessage('userValidateError', { extra: {
                errorObj: errorResponseData
            } });
        }, false);
    }

    // grab users info so we can prepopulate it
    @action getUserInfo() {
        userInfo(loginStore.authToken).then((response) => {
            const { data } = response;
            // if auth token has expired
            if (data.Success === false) {
                loginStore.setLoginError('your session has expired please log in again');
                this.setTimeoutErrorStep('login');
                throw new Error('expired token');
            }

            // if there is no expire date
            if (data.FreeTrialExpiry.length === 0) {
                // trial has been converted to paid don't let the user log in
                this.setCanTrialBeConverted(false);
                loginStore.setLoginError('This account is already associated with a Grasshopper paid plan. Please go to grasshopper.com and log in to make any changes to your account.');
                throw new Error('Trial already converted to paid');
            }

            // try to get trial age from api
            if (this.freeTrialAge === '') this.setTrailAgeFromAPI(deepGet(data, ['FreeTrialBannerLink']));
            // current date
            const currentDate = new Date().getTime();
            // date the user signed up
            const signupDate = new Date(data.FreeTrialExpiry).getTime();
            // date the user signed up for Trial
            const trialSignupDate = new Date(data.ActivationDate).getTime();
            // milliseconds * seconds * mins * hours * days = 19 days
            const trialTime = 1000 * 60 * 60 * 24 * TRIAL_MAX_AGE_EXPIRY_DATE;
            // New Prices Date
            const newPricesDate = new Date('January 11, 2023 00:00:00Z').getTime();
            // if the Trial signup date is before the new prices start date
            if (trialSignupDate < newPricesDate) {
                // Set Trial has old prices flag
                this.setTrialHasOldPrices(true);
            }
            // if time passed is more than trial time
            if (this.freeTrialAge > TRIAL_MAX_AGE_FTAGE || (this.freeTrialAge === '' && currentDate - signupDate > trialTime)) {
                // trial is expired and user can not convert
                this.setCanTrialBeConverted(false);
                this.setTrialExpired(true);
                throw new Error('Trial expired, no number associated with it');
            }
            // if the is transfer is needed flag is not set, try to get it from user details
            if (this.isTransferNeeded === null) this.setIstransferNeededFromAPI(deepGet(data, ['FreeTrialBannerLink']));
            // if no issues set user data
            loginStore.setUserInfo({
                ...data,
                CancelledNumbers: data.CancelledNumbers ? convertNumberFormat(data.CancelledNumbers[0], 'UI') : false
            });
            gtm(loginEvent());
            gtm(userInformationEvent());

            // get users phone number if the account is expired
            if (!loginStore.userInfo.CancelledNumbers) this.getTrialNumberInfo();
            // Call offerSuite with suiteID regarding the Trial signed up date to the new prices date
            offerSuiteStore.getOfferSuite(this.trialHasOldPrices ? OFFER_SUITE_ANNUAL : OFFER_SUITE_WITH_TRUE_SOLO);

            // Trial Users and Extensions Count
            trialUsersCount(loginStore.authToken).then(resp => this.setTrialUsersAndExtensions({
                users: resp.data.length,
                extensions: data.ExtensionIds.length,
                callRecording: data.CallRecordingActive
            }));

            // Expired Trial with 10-28 Days but still gets user details
            if (data.AccountStatus === 'Cancelled') {
                this.setTrialPassiveExpired(true);
            }
            // set converting to paid to true
            this.setIsUserConvertingToPaid(true);
            // close modal
            if (modalStore.childComponentString === 'loginModal') modalStore.setModalOptions({ isModalOpen: false });

            return true;
        }).catch((err) => {
            // clear user info
            this.clearTrialFlow();
            // if error capture it on Sentry
            Sentry.captureMessage('getUserInfoError', { extra: {
                errorObj: err
            } });
            return err;
        });
    }

    @action getTrialNumberInfo() {
        userNumber(loginStore.userInfo.VpsId, loginStore.authToken).then((response) => {
            const { data } = response;

            if (data && data[0]) {
                loginStore.setTrialNumberInfo({
                    ...data[0],
                    Number: convertNumberFormat(data[0].Number, 'UI')
                });
            }
        }).catch((err) => {
            // if error capture it on Sentry
            Sentry.captureMessage('getTrialNumberInfoError', { extra: {
                errorObj: err
            } });
            return err;
        });
    }

    // TODO: rearchitect this to be drier, the same logic happens when validating both api calls, it can be extracted to be handled by 1 function
    // estimate the payment based on plan and user location
    @action estimatePayment(offerSet, locationCallback, genericErrorCallback) {
        const orderTotal = {};
        const estimatePaymentObj = paymentEstimateObject();

        // set calculating taxes
        this.setCalculatingTaxes(true);
        // add monthly plan id to the sign up object
        estimatePaymentObj.OfferId = offerSet[PLAN_TERM_MONTHLY].Id;
        axios.post('/users/ConvertFreeTrialEstimates', estimatePaymentObj, { headers: { Authorization: loginStore.authToken }
        }).then((response) => {
            // validate 200 response with success = false
            if (response.data.Success === false) {
                // if no error message, session has expired
                if (!response.data.ErrorText) {
                    // add error session expired copy to the modal
                    loginStore.setLoginError('your session has expired please log in again');
                    // clear user info
                    this.clearTrialFlow();
                    // Track Error
                    gtm(errorPurchaseEvent('session has expired please log in again'));

                    return this.setTimeoutErrorStep('payment estimate');
                }

                // if trial account not found
                if (response.data.ErrorText === 'Not Free Trial Account') {
                    this.setEstimatePaymentError(PAYMENT_ESTIMATE_NO_TRIAL_ACCOUNT);
                    // Track Error
                    gtm(errorPurchaseEvent(PAYMENT_ESTIMATE_NO_TRIAL_ACCOUNT));

                    return this.setCanTrialBeConverted(false);
                }

                // error is invalid zip
                if (response.data.ErrorText === 'Invalid ZIP Code') {
                    this.setInvalidAddress(true);
                    return locationCallback();
                }

                // else
                this.setInvalidAddress(true);
                genericErrorCallback();

                // return the error
                return new Error(response.data);
            }
            // add response to order total obj
            orderTotal[PLAN_TERM_MONTHLY] = response.data;
            // if there is a matching annual plan
            if (offerSet[PLAN_TERM_ANNUAL]) {
                // replace the monthly plan ID for the annual plan ID
                estimatePaymentObj.OfferId = offerSet[PLAN_TERM_ANNUAL].Id;
                // estimate annual prices
                axios.post('/users/ConvertFreeTrialEstimates', estimatePaymentObj, { headers: { Authorization: loginStore.authToken } })
                    .then((resp) => {
                        // validate 200 response with success = false
                        if (resp.data.Success === false) {
                            // if no error message, session has expired
                            if (!resp.data.ErrorText) {
                                // add error session expired copy to the modal
                                loginStore.setLoginError('your session has expired please log in again');
                                // clear user info
                                this.clearTrialFlow();
                                // Track Error
                                gtm(errorPurchaseEvent('session has expired please log in again'));

                                return this.setTimeoutErrorStep('payment estimate');
                            }

                            // if trial account not found
                            if (resp.data.ErrorText === 'Not Free Trial Account') {
                                this.setEstimatePaymentError(PAYMENT_ESTIMATE_NO_TRIAL_ACCOUNT);
                                // Track Error
                                gtm(errorPurchaseEvent(PAYMENT_ESTIMATE_NO_TRIAL_ACCOUNT));

                                return this.setCanTrialBeConverted(false);
                            }

                            // error is invalid zip
                            if (response.data.ErrorText === 'Invalid ZIP Code') {
                                this.setInvalidAddress(true);
                                return locationCallback();
                            }

                            // else
                            this.setInvalidAddress(true);
                            genericErrorCallback();

                            // return the error
                            return new Error(response.data);
                        }
                        // add response to the order total
                        orderTotal[PLAN_TERM_ANNUAL] = resp.data;
                        // set order total
                        return this.setOrderTotal(orderTotal);
                    }).catch((err) => {
                        return err;
                    });
            } else {
                // set order total
                this.setOrderTotal(response.data);
            }

            return true;
        }).catch((err) => {
            // add error session expired copy to the modal
            loginStore.setLoginError('your session has expired please log in again');
            // clear user info
            this.clearTrialFlow();
            // set error step
            this.setTimeoutErrorStep('payment estimate');
            // Track Error
            gtm(errorPurchaseEvent('session has expired please log in again'));
            // track error in Sentry
            Sentry.captureMessage('estimatePaymentError', { extra: {
                errorObj: err.response.data || err
            } });
            return err;
        }).then(() => {
            // set calculating taxes to false
            this.setCalculatingTaxes(false);
        });
    }

    // estimate the payment  based on plan and user location
    @action convertFreeTrial(requestBody, successCallback) {
        // open up creating account modal
        this.setCreatingAccount(true);
        axios.post('/users/ConvertFreeTrial', requestBody, { headers: { Authorization: loginStore.authToken } })
            .then((response) => {
                successCallback(response);
            })
            .catch((err) => {
                // add error session expired copy to the modal
                loginStore.setLoginError('your session has expired please log in again');
                // clear user info
                this.clearTrialFlow();
                // set error step
                this.setTimeoutErrorStep('payment estimate');
                // Track Error
                gtm(errorPurchaseEvent('session has expired please log in again'));
                // track error in Sentry
                Sentry.captureMessage('convertToPaidError', {
                    extra: {
                        errorMessage: 'error not tracked',
                        error: err
                    }
                });
            })
            .then(() => {
                this.setCreatingAccount(false);
            });
    }
    // check if taxes are total amount is calculated
    @computed get calculatedTotal() {
        return isPlainObject(this.orderTotal) && this.orderTotal[PLAN_TERM_MONTHLY] && this.orderTotal[PLAN_TERM_MONTHLY].TotalAmount;
    }

    @computed get isTransferNeededBool() {
        return this.isTransferNeeded === null ? false : this.isTransferNeeded;
    }
}

const convertToPaidStore = new ConvertToPaidClass();
export { convertToPaidStore, ConvertToPaidClass };
